import React, { useState, useCallback, useEffect } from 'react'
import { message, Progress, Button, Input, Radio, Modal } from 'antd'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import {
  UploadOutlined,
  EyeOutlined,
  CloseOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  ArrowRightOutlined,
  TranslationOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  InfoCircleOutlined
} from '@ant-design/icons'
import Dropzone from 'react-dropzone'
import PropTypes from 'prop-types'
import './UploadDocument.scss'
import StoryCreationForm from './StoryCreationForm'
import StepTitle from '../StepTitle/StepTitle'
import actions from '../../../../store/actions'
import pdfIcon from './img/pdfIcon.svg'

const MAX_PDF_SIZE = 10 * 1024 * 1024
const MAX_PDF_PAGES = 100

const readFileAsArrayBuffer = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(file)
  })

const decodePdfText = arrayBuffer => {
  const bytes =
    arrayBuffer instanceof Uint8Array
      ? arrayBuffer
      : new Uint8Array(arrayBuffer)

  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder('latin1').decode(bytes)
  }

  let text = ''
  const chunkSize = 8192

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize)
    text += String.fromCharCode.apply(null, chunk)
  }

  return text
}

const getLinearizedPageCount = text => {
  const linearizedMatch = text.match(/\/Linearized\b[\s\S]{0,512}?\/N\s+(\d+)/)

  return linearizedMatch ? Number(linearizedMatch[1]) : 0
}

const getMaxPageTreeCount = text => {
  const pageTreeObjects = text.match(/\d+\s+\d+\s+obj[\s\S]*?endobj/g) || []
  const objectCounts = pageTreeObjects.map(objectText => {
    if (!/\/Type\s*\/Pages\b/.test(objectText)) {
      return 0
    }

    const countMatch = objectText.match(/\/Count\s+(\d+)/)

    return countMatch ? Number(countMatch[1]) : 0
  })
  const nearbyCounts = []
  const typeBeforeCountRegex = /\/Type\s*\/Pages\b[\s\S]{0,1000}?\/Count\s+(\d+)/g
  const countBeforeTypeRegex = /\/Count\s+(\d+)[\s\S]{0,1000}?\/Type\s*\/Pages\b/g
  let match

  while ((match = typeBeforeCountRegex.exec(text))) {
    nearbyCounts.push(Number(match[1]))
  }

  while ((match = countBeforeTypeRegex.exec(text))) {
    nearbyCounts.push(Number(match[1]))
  }

  return Math.max(0, ...objectCounts, ...nearbyCounts)
}

const getDecodedPdfStreamText = async (bytes, text) => {
  const DecompressionStreamCtor =
    typeof window !== 'undefined' ? window.DecompressionStream : null

  if (
    !DecompressionStreamCtor ||
    typeof Blob === 'undefined' ||
    typeof Response === 'undefined'
  ) {
    return ''
  }

  const decodedStreams = []
  const streamRegex = /\bstream(?:\r\n|\n|\r)/g
  let match

  while ((match = streamRegex.exec(text))) {
    const streamStart = match.index + match[0].length
    let streamEnd = text.indexOf('endstream', streamStart)

    if (streamEnd === -1) {
      break
    }

    while (streamEnd > streamStart && /\s/.test(text[streamEnd - 1])) {
      streamEnd -= 1
    }

    const headerStart = Math.max(0, text.lastIndexOf('obj', match.index))
    const streamHeader = text.slice(headerStart, match.index)

    if (!/\/FlateDecode\b/.test(streamHeader)) {
      continue
    }

    try {
      const compressedStream = bytes.subarray(streamStart, streamEnd)
      const decompressedStream = new Blob([compressedStream])
        .stream()
        .pipeThrough(new DecompressionStreamCtor('deflate'))
      const decodedBuffer = await new Response(decompressedStream).arrayBuffer()
      decodedStreams.push(decodePdfText(decodedBuffer))
    } catch (error) {
      // Some streams are not page metadata. Ignore streams the browser cannot decode.
    }
  }

  return decodedStreams.join('\n')
}

const getPdfPageCount = async file => {
  const arrayBuffer = await readFileAsArrayBuffer(file)
  const bytes = new Uint8Array(arrayBuffer)
  const rawText = decodePdfText(bytes)
  const decodedStreamText = await getDecodedPdfStreamText(bytes, rawText)
  const text = `${rawText}\n${decodedStreamText}`
  const pageMatches = text.match(/\/Type\s*\/Page\b/g) || []

  return Math.max(
    pageMatches.length,
    getMaxPageTreeCount(text),
    getLinearizedPageCount(text)
  )
}

const isPdfFile = file =>
  file?.type === 'application/pdf' || /\.pdf$/i.test(file?.name || '')

const formatFileSize = bytes => {
  if (!bytes) return '0 MB'

  const sizeInMb = bytes / (1024 * 1024)
  const formattedSize = Number.isInteger(sizeInMb)
    ? String(sizeInMb)
    : sizeInMb.toFixed(1)

  return `${formattedSize} MB`
}

const UploadDocument = ({
  formData,
  onValidationChange,
  setFormData,
  onProceed,
  library,
  upload,
  clearUploadList,
  videoId,
  onBeforeAddStory
}) => {
  const [isUploading, setIsUploading] = useState(false)
  const [isStoryFormVisible, setIsStoryFormVisible] = useState(false)
  const [isTranslation, setIsTranslation] = useState(false)
  const [openInEditMode, setOpenInEditMode] = useState(false)
  const [inputMethod, setInputMethod] = useState('upload')
  const [directText, setDirectText] = useState('')
  const [uploadError, setUploadError] = useState('')
  const [uploadDetails, setUploadDetails] = useState(null)

  // Initialize component state from formData
  useEffect(() => {
    const { file, voiceoverText } = formData
    if (!file && !voiceoverText) return

    if (
      typeof voiceoverText === 'string' &&
      voiceoverText &&
      voiceoverText !== directText
    ) {
      setDirectText(voiceoverText)
    }
    setInputMethod(file ? 'upload' : 'text')
  }, [formData])

  useEffect(() => {
    onValidationChange(!!formData.voiceoverText)
  }, [formData.voiceoverText])

  const onDropFile = async (dropped, rejected = []) => {
    clearUploadList()
    setUploadError('')
    setUploadDetails(null)

    if (rejected.length > 0) {
      setUploadError(
        `Please upload a PDF file under 10 MB with ${MAX_PDF_PAGES} pages or fewer.`
      )
      return
    }

    if (!dropped || dropped.length === 0) {
      return
    }

    const invalidTypeFiles = dropped.filter(file => !isPdfFile(file))

    if (invalidTypeFiles.length > 0) {
      setUploadError('Only PDF files can be uploaded.')
      return
    }

    const oversizedFiles = dropped.filter(file => file.size > MAX_PDF_SIZE)

    if (oversizedFiles.length > 0) {
      setUploadError('PDF file size must be 10 MB or less.')
      return
    }

    let selectedFileDetails = null

    for (const file of dropped) {
      try {
        const pageCount = await getPdfPageCount(file)

        if (!pageCount) {
          setUploadError(
            'Could not read the PDF page count. Please try another PDF.'
          )
          return
        }

        if (pageCount > MAX_PDF_PAGES) {
          setUploadError(
            `PDF must have ${MAX_PDF_PAGES} pages or fewer. This file has ${pageCount} pages.`
          )
          return
        }

        selectedFileDetails = {
          name: file.name,
          size: file.size,
          pageCount
        }
      } catch (error) {
        setUploadError('Could not validate the PDF. Please try another PDF.')
        return
      }
    }

    setUploadDetails(selectedFileDetails)
    setIsUploading(true)

    try {
      const response = await upload(dropped[0])
      const newFile = {
        uid: response.id,
        name: response.filename,
        status: 'done',
        url: response.link,
        path: response.path,
        pageCount: selectedFileDetails?.pageCount,
        size: selectedFileDetails?.size
      }
      setFormData(prev => ({ ...prev, file: newFile }))
    } catch (error) {
      setUploadError('Failed to upload file. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemove = file => {
    setFormData(prev => ({ ...prev, file: null }))
    setUploadError('')
    setUploadDetails(null)
    onValidationChange(!!formData.voiceoverText)
  }

  const handleView = link => {
    window.open(link, '_blank').focus()
  }

  const handleCloseStoryForm = () => {
    setIsStoryFormVisible(false)
    setIsTranslation(false)
    setOpenInEditMode(false)
  }

  const handleAddStory = (story, languageCode, options = {}) => {
    const safeStory =
      typeof story === 'string'
        ? story
        : typeof directText === 'string'
          ? directText
          : ''

    setIsStoryFormVisible(false)
    setOpenInEditMode(false)
    setDirectText(safeStory)
    setFormData(prev => ({
      ...prev,
      voiceoverText: safeStory,
      language: languageCode || prev.language || 'en',
      // Clear file if we're in text mode
      file: inputMethod === 'text' ? null : prev.file
    }))
    onValidationChange(!!safeStory)

    if (options.proceedToNextStep && safeStory.trim()) {
      onProceed({
        voiceoverText: safeStory,
        language: languageCode || formData.language || 'en',
        file: inputMethod === 'text' ? null : formData.file
      })
    }
  }

  const handleDirectTextChange = text => {
    setDirectText(text)
  }
  const handleOpenStoryForm = () => {
    if (inputMethod === 'text' && !directText.trim()) {
      message.error('Please enter your script text first')
      return
    }

    setIsTranslation(false)
    setOpenInEditMode(false)
    setIsStoryFormVisible(true)
  }

  const handleUseTextAsStory = () => {
    if (!directText.trim()) {
      message.error('Please enter your script text first')
      return
    }

    setIsTranslation(false)
    setOpenInEditMode(true)
    setIsStoryFormVisible(true)
  }

  const handleOpenTranslationForm = () => {
    setIsTranslation(true)
    setOpenInEditMode(false)
    setIsStoryFormVisible(true)
  }

  return (
    <div className="upload-document">
      <StepTitle
        iconType="file-text"
        title="Upload Document"
        description="Please upload a PDF document with your video script or provide your
          script text directly. You'll need to finalize your script in the Story Form."
        extra={
          <Button
            className="upload-document__button translate-header-btn"
            onClick={handleOpenTranslationForm}
          >
            <TranslationOutlined /> Translate
          </Button>
        }
      />

      <div className="upload-document__input-selector">
        <Radio.Group
          value={inputMethod}
          onChange={e => {
            setUploadError('')
            setInputMethod(e.target.value)
          }}
          buttonStyle="solid"
          size="large"
          className="upload-document__input-selector-radio"
        >
          <Radio.Button value="upload">PDF Document</Radio.Button>
          <Radio.Button value="text">Direct Text Input</Radio.Button>
        </Radio.Group>
      </div>

      {inputMethod === 'upload' ? (
        <div className="upload-file-main">
          <div className="upload-file-main__summary">
            <div>
              <h3>Script PDF</h3>
            </div>
            <div
              className="upload-file-main__limits"
              aria-label="Upload limits"
            >
              <span>PDF</span>
              <span>{formatFileSize(MAX_PDF_SIZE)} max</span>
              <span>{MAX_PDF_PAGES} pages max</span>
            </div>
          </div>

          {!formData.file ? (
            <Dropzone
              accept="application/pdf"
              maxSize={MAX_PDF_SIZE}
              multiple={false}
              onDrop={onDropFile}
              disabled={isUploading}
              className={`upload-file-main-action ${uploadError ? 'has-error' : ''
                }`}
              activeClassName="active"
            >
              {({ isDragActive }) => (
                <>
                  <div className="upload-file-main-action-right">
                    <div className="upload-file-main-action-right-icon">
                      <FileTextOutlined />
                    </div>
                  </div>

                  <div className="upload-file-main-action-center">
                    <div className="upload-file-main-action-center-title">
                      {isDragActive ? 'Drop to upload' : 'Drag PDF here'}
                    </div>
                    <div className="upload-file-main-action-center-copy">
                      or click to browse
                    </div>
                  </div>

                  <div className="upload-file-main-action-btn">
                    {isUploading ? (
                      <SyncOutlined
                        spin
                        style={{ color: '#5B6EE2', fontSize: 16 }}
                      />
                    ) : (
                      <UploadOutlined
                        style={{ color: '#5B6EE2', fontSize: 16 }}
                      />
                    )}
                  </div>
                </>
              )}
            </Dropzone>
          ) : (
            <div className="upload-file-main-filediv">
              <div className="upload-file-main-filediv-right">
                <div className="upload-file-main-filediv-right-icon">
                  <img src={pdfIcon} alt="pdfIcon" />
                </div>
                <div className="upload-file-main-filediv-right-content">
                  <div>
                    <div className="upload-file-main-filediv-right-content-title">
                      {formData.file.name}
                    </div>
                    <div className="upload-file-main-filediv-right-content-size">
                      {formData.file.pageCount
                        ? `${formData.file.pageCount} pages`
                        : 'PDF ready'}
                      {formData.file.size
                        ? ` - ${formatFileSize(formData.file.size)}`
                        : ''}
                    </div>
                  </div>
                </div>
              </div>

              <div className="upload-file-main-filediv-btn">
                <div>
                  <EyeOutlined
                    style={{ color: '#5B6EE2', fontSize: 16 }}
                    onClick={() => handleView(formData.file.url)}
                  />
                </div>
                <div>
                  <CloseOutlined
                    style={{ color: 'red', fontSize: 16 }}
                    onClick={() => handleRemove(formData.file)}
                  />
                </div>
              </div>
            </div>
          )}

          {uploadError && (
            <div className="upload-file-main__feedback upload-file-main__feedback--error">
              <InfoCircleOutlined />
              <span>{uploadError}</span>
            </div>
          )}

          {!uploadError && uploadDetails && !isUploading && !formData.file && (
            <div className="upload-file-main__feedback upload-file-main__feedback--success">
              <CheckCircleOutlined />
              <span>
                PDF validated: {uploadDetails.pageCount} pages,{' '}
                {formatFileSize(uploadDetails.size)}.
              </span>
            </div>
          )}

          {isUploading &&
            library.uploadProgress.map((file, i) => (
              <div key={i}>
                <div className="upload-file-main-filediv">
                  <div className="upload-file-main-filediv-right">
                    <div className="upload-file-main-filediv-right-icon">
                      <img src={pdfIcon} alt="pdfIcon" />
                    </div>
                    <div className="upload-file-main-filediv-right-content">
                      <div>
                        <div className="upload-file-main-filediv-right-content-title">
                          {file.name}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {file.percent > 0 && <Progress percent={file.percent} />}
              </div>
            ))}

          {formData.file && (
            <div className="upload-document__actions upload-document__actions--upload">
              <div className="upload-document__ready-note">
                <CheckCircleOutlined />
                <span>Document is ready for story generation.</span>
              </div>
              <Button
                className="upload-document__button"
                type="primary"
                onClick={handleOpenStoryForm}
              >
                <ThunderboltOutlined /> Generate Story from PDF
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="upload-document__text-input">
          <Input.TextArea
            value={directText}
            onChange={e => handleDirectTextChange(e.target.value)}
            placeholder="Please enter your video script text here"
            autoSize={{ minRows: 8, maxRows: 20 }}
            className="upload-document__textarea"
          />
          <div className="upload-document__text-actions">
            <Button
              type="primary"
              onClick={handleOpenStoryForm}
              className="upload-document__confirm-button"
              disabled={!directText?.trim()}
            >
              <ThunderboltOutlined />
              Generate Story with AI
            </Button>

            <Button
              type="default"
              onClick={handleUseTextAsStory}
              className="upload-document__confirm-button"
              disabled={!directText?.trim()}
              style={{ marginRight: 8 }}
            >
              Use Text as Story <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      )}

      {isStoryFormVisible && (
        <StoryCreationForm
          inputMethod={inputMethod}
          uploadedFile={inputMethod === 'upload' ? formData.file : null}
          initialText={directText}
          onCancel={handleCloseStoryForm}
          onAddStory={handleAddStory}
          videoId={videoId}
          isTranslation={isTranslation}
          existingStory={
            formData.voiceoverText ||
            (isTranslation ? directText : '')
          }
          onBeforeAddStory={onBeforeAddStory}
          startInEditMode={openInEditMode}
        />
      )}
    </div>
  )
}

UploadDocument.propTypes = {
  onValidationChange: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  library: PropTypes.object.isRequired,
  upload: PropTypes.func.isRequired,
  clearUploadList: PropTypes.func.isRequired
}

const mapStateToProps = state => ({
  library: state.library
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      upload: actions.library.upload,
      clearUploadList: actions.library.clearUploadList
    },
    dispatch
  )

export default connect(mapStateToProps, mapDispatchToProps)(UploadDocument)
